import React, { Component } from 'react';
import { connect } from 'react-redux';
import filter from 'lodash/filter';
import reduce from 'lodash/reduce';
import isEqual from 'lodash/isEqual';
import some from 'lodash/some';
import map from 'lodash/map';
import find from 'lodash/find';
import concat from 'lodash/concat';
import {
  Container,
  Table,
  TableHeader,
  PlainButton,
  PaddingWrapper,
} from 'components/styled-components';
import {
  Section,
  TableRow,
  Title,
  TitleWrapper,
  Wrapper,
} from 'containers/Manage-Questionnaire/styled-components';
import Pagination from 'components/pagination';
import colors from 'utils/colors';
import Loading from 'components/loading';
import PrescreenRow from 'containers/Manage-Questionnaire/mapping/PrescreenRow';
import QuestionnaireRow from 'containers/Manage-Questionnaire/mapping/QuestionnaireRow';
import shortid from 'shortid';
import { fetchPrescreenQuestions } from 'actions/prescreen';
import { fetchQuestionnaireQuestions } from 'actions/questionnaire';
import { publishPrescreenQuestions } from 'api/prescreen';
import { publishQuestionnaireQuestions } from 'api/questionnaire';
import { AppToaster } from 'components/AppToaster';
import { Intent } from '@blueprintjs/core';

// checks if any questions from the list of questions contains a draft - used to choose whether or not to display the Publish button
const containsDrafts = questions => some(questions, ['versionStatus', 'Draft']);

// checks if any answers from the list of questions contains a draft - used to choose whether or not to display the Publish button
const answersContainDrafts = questions => {
  const bool = some(questions, q =>
    some(q.answers, ['versionStatus', 'Draft'])
  );
  return bool;
};

const fields = [
  'Mapped Questions',
  'Control Number',
  'Question Type',
  'Display Category',
  'Weight',
];

// removes an active question from the collection if there is already a draft in progress for that question
// because active questions that have an active draft can no longer be edited, all edits should be performed on the draft.
const removeActiveWithDraft = (collection, idKey) => {
  const draftIDs = collection
    .filter(item => item.versionStatus === 'Draft')
    .map(item => {
      return item.originalID ? item.originalID : item[idKey];
    });

  return collection.filter(
    item =>
      !(
        (draftIDs.includes(item.originalID) ||
          draftIDs.includes(item[idKey])) &&
        item.versionStatus === 'Active'
      )
  );
};

// populates a collection of arrays that are keyed by display category to display on the page
const generateCategoryBucket = (categories, prescreen, questionnaire) =>
  reduce(
    categories,
    (result, value) => {
      // filter out the active prescreen questions that currently have a draft
      const filteredPrescreen = removeActiveWithDraft(prescreen, 'questionID');
      const answers = reduce(
        filteredPrescreen,
        (collection, pre) => {
          // check if the answers of a prescreen question are tied to the display category currently being iterated over
          const answers = filter(pre.answers, a =>
            some(a.displayCategory, dc =>
              isEqual(dc.categoryID, value.categoryID)
            )
          );
          // return the collection as is if there are no matches
          if (answers.length === 0) {
            return collection;
          } else {
            // add the prescreen question to the collection if there is a match
            collection.push({
              question: pre.question,
              questionID: pre.questionID,
              answerLimit: pre.answerLimit,
              answers,
              versionStatus: pre.versionStatus,
            });
            return collection;
          }
        },
        []
      );

      // rinse & repeat for questionnaire
      const filteredQuestions = removeActiveWithDraft(
        questionnaire,
        'questionID'
      );
      const questions = filter(
        filteredQuestions,
        q => q.displayCategoryID === value.categoryID
      );
      if (answers.length === 0 || questions.length === 0) {
        return result;
      } else {
        result.push({ value, answers, questions });
        return result;
      }
    },
    []
  );

class Mapping extends Component {
  state = {
    currentPage: 0,
    perPage: 10,
    mapped: [],
    prescreenIndex: null,
    questionnaireIndex: null,
    isPrescreenOpen: false,
    isQuestionnaireOpen: false,
  };

  componentDidMount() {
    window.scrollTo(0, 0);
    this.props.fetchPrescreenQuestions({ start: 0, limit: -1 });
    this.props.fetchQuestionnaireQuestions({ start: 0, limit: -1 });
    const mapped = generateCategoryBucket(
      this.props.categories,
      this.props.prescreen.result,
      this.props.questions.result
    );
    this.setState({ mapped });
  }

  componentDidUpdate(prevProps) {
    if (
      !isEqual(prevProps.prescreen.result, this.props.prescreen.result) ||
      !isEqual(prevProps.questions.result, this.props.questions.result)
    ) {
      const mapped = generateCategoryBucket(
        this.props.categories,
        this.props.prescreen.result,
        this.props.questions.result
      );
      this.setState({ mapped });
    }
  }

  handlePageClick = currentPage => {
    window.scrollTo(0, 0);
    this.setState({ currentPage });
  };

  handlePerPageChange = perPage => {
    window.scrollTo(0, 0);
    this.setState({ perPage: perPage.value, currentPage: 0 });
  };

  // index is used to go back and forth while in the editing drawer
  setPrescreenIndex = prescreenIndex => this.setState({ prescreenIndex });
  setQuestionnaireIndex = questionnaireIndex =>
    this.setState({ questionnaireIndex });

  handlePublish = () => {
    publishPrescreenQuestions()
      .then(() => {
        publishQuestionnaireQuestions()
          .then(res => {
            AppToaster.show({
              message: res.data.message,
              intent: Intent.SUCCESS,
            });
            this.props.fetchQuestionnaireQuestions({ start: 0, limit: -1 });
          })
          .catch(err => {
            AppToaster.show({
              message: err.response.data.message,
              intent: Intent.DANGER,
            });
          });
        this.props.fetchPrescreenQuestions({ start: 0, limit: -1 });
      })
      .catch(err => {
        AppToaster.show({
          message: err.response.data.message,
          intent: Intent.DANGER,
        });
      });
    this.setState({ currentPage: 0 });
  };

  renderMappingList = () => {
    const {
      perPage,
      currentPage,
      mapped,
      prescreenIndex,
      questionnaireIndex,
    } = this.state;

    return map(mapped, (mapping, i) => {
      /* Pagination logic:
         if the index is NOT outside the bounds of the current page range,
         then display the mapped pair. else, don't display the pair */
      return i < perPage * currentPage ||
        i >= perPage * (currentPage + 1) ? null : (
        <Container background={colors.whiteGray} key={i}>
          <Wrapper>
            <TitleWrapper>
              <Title>
                Display Category: <strong>{mapping.value.categoryName}</strong>
              </Title>
            </TitleWrapper>
            <Section>
              <TitleWrapper>
                <Title>Pre-Screen Answers</Title>
              </TitleWrapper>
              <Table>
                <tbody>
                  <TableRow>
                    <TableHeader>Mapped Answers</TableHeader>
                    <TableHeader>Pre-Screen Question</TableHeader>
                    <TableHeader>
                      {/*Blank to extend border for Edit Drawer */}
                    </TableHeader>
                  </TableRow>
                  {map(mapping.answers, (q, index) => (
                    <PrescreenRow
                      key={shortid.generate()}
                      question={q}
                      questionForDrawer={find(this.props.prescreen.result, [
                        'questionID',
                        q.questionID,
                      ])}
                      index={{ parent: i, index }}
                      setPrescreenIndex={this.setPrescreenIndex}
                      currentIndex={prescreenIndex}
                      total={mapping.answers.length}
                      fetchPrescreenQuestions={() =>
                        this.props.fetchPrescreenQuestions({
                          start: 0,
                          limit: -1,
                        })
                      }
                    />
                  ))}
                </tbody>
              </Table>
            </Section>
            <Section>
              <TitleWrapper>
                <Title>Questionnaire Questions</Title>
              </TitleWrapper>
              <Table>
                <tbody>
                  <TableRow>
                    {map(fields, field => (
                      <TableHeader key={shortid.generate()}>
                        {field}
                      </TableHeader>
                    ))}
                    {/*Blank to extend border for Edit Drawer */}
                    <TableHeader />
                  </TableRow>
                  {map(mapping.questions, (q, index) => (
                    <QuestionnaireRow
                      key={shortid.generate()}
                      question={q}
                      index={{ parent: i, index }}
                      setQuestionnaireIndex={this.setQuestionnaireIndex}
                      currentIndex={questionnaireIndex}
                      total={mapping.questions.length}
                      questionTypes={this.props.questionTypes}
                      categories={this.props.categories}
                      fetchQuestionnaireQuestions={() =>
                        this.props.fetchQuestionnaireQuestions({
                          start: 0,
                          limit: -1,
                        })
                      }
                    />
                  ))}
                </tbody>
              </Table>
            </Section>
          </Wrapper>
        </Container>
      );
    });
  };
  render() {
    if (this.props.isFetching) return <Loading size={200} />;
    return (
      <div>
        <PaddingWrapper>
          {(containsDrafts(
            concat(this.props.questions.result, this.props.prescreen.result)
          ) ||
            answersContainDrafts(
              concat(this.props.questions.result, this.props.prescreen.result)
            )) && (
            <PaddingWrapper padding="0 0 1rem 1rem">
              <PlainButton
                onClick={this.handlePublish}
                style={{ marginRight: '2rem' }}
              >
                PUBLISH
              </PlainButton>
            </PaddingWrapper>
          )}
        </PaddingWrapper>
        {this.renderMappingList()}
        {this.state.mapped && (
          <Pagination
            numRecords={this.state.mapped.length}
            perPage={this.state.perPage}
            currentPage={this.state.currentPage}
            onPageClick={this.handlePageClick}
            handlePerPageChange={this.handlePerPageChange}
          />
        )}
      </div>
    );
  }
}

const mapStateToProps = state => ({
  prescreen: state.prescreen.questions,
  questions: state.questionnaire.questions,
  questionTypes: state.utils.questionTypes,
  categories: state.utils.categories,
  isFetchingCategories: state.utils.isFetchingCategories,
  isFetchingTypes: state.utils.isFetchingTypes,
  appUser: state.auth.appUser,
});

Mapping = connect(
  mapStateToProps,
  {
    fetchPrescreenQuestions,
    fetchQuestionnaireQuestions,
  }
)(Mapping);

export default Mapping;
